import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FolderOpen, Users2, ArrowUpRight, FileText, Activity as ActivityIcon } from "lucide-react";
import { useDriveAuth } from "../context/DriveAuthContext";
import { fetchFolders, fetchFiles, fetchActivity } from "../lib/driveClient";

const ACTION_LABELS = {
  upload_file: "hat Datei hochgeladen",
  download_file: "hat Datei heruntergeladen",
  delete_file: "hat Datei gelöscht",
  create_folder: "hat Ordner erstellt",
  create_artist: "hat Künstler angelegt",
  create_customer: "hat Kunden angelegt",
};

export default function DriveHub() {
  const { profile, isStaff, role } = useDriveAuth();
  const [stats, setStats] = useState({ folders: 0, files: 0 });
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [f, fi, act] = await Promise.all([
          fetchFolders().catch(() => []),
          fetchFiles(null).catch(() => []),
          fetchActivity(6).catch(() => []),
        ]);
        setStats({ folders: f.length, files: fi.length });
        setActivities(act);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-10">
      {/* Welcome */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="flex items-center gap-3 mb-3 text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-bold">
          <span className="block w-8 h-px bg-[#ff0033] shadow-[0_0_8px_#ff0033]" />
          Straßenwert Drive
        </div>
        <h1 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-6xl">
          Wohin<br />
          <span className="text-glow-red-strong">willst du?</span>
        </h1>
        <p className="mt-4 text-sm md:text-base text-zinc-500 max-w-xl">
          Zwei Wege. Deine Wahl — <strong className="text-zinc-300">Firmen-Verwaltung</strong> für Dokumente & Ordner, oder <strong className="text-zinc-300">CRM</strong> für Künstler, Kunden & Verträge.
        </p>
      </motion.div>

      {/* Two big Hub Tiles */}
      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        {/* Tile 1: Firmen-Verwaltung */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <Link
            to="/drive/files"
            data-testid="hub-files"
            className="group relative block bg-[#0A0A0A] border border-white/[0.08] p-8 md:p-10 min-h-[280px] md:min-h-[340px] overflow-hidden hover:border-[#ff0033]/50 transition-all hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(255,0,51,0.4)]"
          >
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#ff0033]/20 blur-[100px] group-hover:bg-[#ff0033]/35 transition-colors duration-700" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 border border-[#ff0033]/40 bg-[#ff0033]/[0.06]">
                  <FolderOpen size={22} className="text-[#ff0033]" />
                </div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-bold">01</div>
              </div>
              <h2 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl leading-[0.95] mb-4">
                Firmen-<br />Verwaltung
              </h2>
              <p className="text-sm text-zinc-400 leading-relaxed max-w-md">
                Dokumente, Verträge, Buchhaltung, Firmendaten. Vollständiges Datei-Management mit Ordnerstruktur, Upload & globaler Suche — wie bei Dropbox, nur auf uns zugeschnitten.
              </p>

              <div className="absolute bottom-0 right-0 flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-zinc-500 group-hover:text-[#ff0033] font-bold transition-colors">
                Öffnen <ArrowUpRight size={14} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Tile 2: CRM */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <Link
            to={isStaff ? "/drive/crm" : "/drive/files"}
            data-testid="hub-crm"
            className={`group relative block bg-[#0A0A0A] border border-white/[0.08] p-8 md:p-10 min-h-[280px] md:min-h-[340px] overflow-hidden transition-all ${
              isStaff
                ? "hover:border-[#ff0033]/50 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(255,0,51,0.4)]"
                : "opacity-60 cursor-not-allowed"
            }`}
            onClick={(e) => {
              if (!isStaff) e.preventDefault();
            }}
          >
            <div className="absolute -top-16 -left-16 w-56 h-56 rounded-full bg-[#ff0033]/15 blur-[100px] group-hover:bg-[#ff0033]/30 transition-colors duration-700" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 border border-[#ff0033]/40 bg-[#ff0033]/[0.06]">
                  <Users2 size={22} className="text-[#ff0033]" />
                </div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-bold">02</div>
              </div>
              <h2 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl leading-[0.95] mb-4">
                CRM &<br />Kontakte
              </h2>
              <p className="text-sm text-zinc-400 leading-relaxed max-w-md">
                Künstler & Kunden anlegen, Daten pflegen, Verträge erstellen. Verträge werden automatisch in die Firmen-Ordner unter <span className="text-[#ff0033]">Verträge → [Typ]</span> nach Monat archiviert.
              </p>

              <div className="absolute bottom-0 right-0 flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-zinc-500 group-hover:text-[#ff0033] font-bold transition-colors">
                {isStaff ? "Öffnen" : "Kein Zugriff"} <ArrowUpRight size={14} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </div>
          </Link>
        </motion.div>
      </div>

      {/* Compact stats + recent activity */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-[#0A0A0A] border border-white/[0.06] p-5">
          <FolderOpen size={16} className="text-[#ff0033] mb-3" />
          <div className="font-display font-black text-2xl">{loading ? "–" : stats.folders}</div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mt-1">Ordner</div>
        </div>
        <div className="bg-[#0A0A0A] border border-white/[0.06] p-5">
          <FileText size={16} className="text-[#ff0033] mb-3" />
          <div className="font-display font-black text-2xl">{loading ? "–" : stats.files}</div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mt-1">Dateien</div>
        </div>
        <div className="bg-[#0A0A0A] border border-white/[0.06] p-5">
          <ActivityIcon size={16} className="text-[#ff0033] mb-3" />
          <div className="font-display font-black text-2xl">{loading ? "–" : activities.length}</div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mt-1">Letzte Events</div>
        </div>
      </div>

      {/* Activity strip */}
      {!loading && activities.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-4 text-[10px] tracking-[0.4em] uppercase text-zinc-500 font-bold">
            <ActivityIcon size={12} className="text-[#ff0033]" />
            Letzte Aktivitäten
          </div>
          <div className="bg-[#0A0A0A] border border-white/[0.06] divide-y divide-white/[0.04]">
            {activities.map((a) => (
              <div key={a.id} className="p-4 md:px-5 flex items-center gap-4 text-sm">
                <span className="red-led shrink-0" />
                <div className="flex-1 min-w-0 truncate text-zinc-400">
                  {ACTION_LABELS[a.action] || a.action}
                  {a.target_name && <span className="text-white ml-2">{`„${a.target_name}"`}</span>}
                </div>
                <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-600 shrink-0">
                  {new Date(a.created_at).toLocaleDateString("de-DE")}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Role hint (only for lower roles) */}
      {role !== "superadmin" && role !== "staff" && (
        <div className="text-xs text-zinc-500">
          Dein Zugriff ist beschränkt auf freigegebene Dateien und deinen eigenen Bereich.
          Wende dich an einen Admin, falls du weitere Rechte brauchst.
        </div>
      )}
    </div>
  );
}
